import { createContext, useContext, useEffect, useState } from "react";

const L2C = { a: "а", b: "б", c: "ц", "č": "ч", "ć": "ћ", d: "д", "đ": "ђ", e: "е", f: "ф", g: "г", h: "х", i: "и", j: "ј", k: "к", l: "л", m: "м", n: "н", o: "о", p: "п", r: "р", s: "с", "š": "ш", t: "т", u: "у", v: "в", z: "з", "ž": "ж" };
const DI = { "dž": "џ", lj: "љ", nj: "њ" };

const DE_TERMS = /^(Kindergeld|Elterngeld|Elternzeit|Bürgergeld|Anmeldung|Lohnabrechnung|Aufenthaltstitel|Aufenthaltserlaubnis|Niederlassungserlaubnis|Ausländerbehörde|Jobcenter|Arbeitsagentur|Bundesagentur|Steuer\w*|Finanzamt|Rente\w*|Krankenkasse|Mindestlohn|Bundestag|Bundesrat|Bundesgesetzblatt|Wohngeld|Kita|Impressum\w*|Datenschutz\w*|USt|IdNr|DDG|MStV|DSGVO|GDPR|E|Mail|Blue|Card|Bundesregierung|Make|it|in|Germany|Close)$/;

function word(w) {
  if (/[qwxyäöüß]/i.test(w) || /balkanpuls/i.test(w) || (w !== "in" && w !== "it" && DE_TERMS.test(w))) return w;
  let out = "";
  for (let i = 0; i < w.length; i++) {
    const two = w.slice(i, i + 2).toLowerCase();
    const up = w[i] !== w[i].toLowerCase();
    if (DI[two]) {
      out += up ? DI[two].toUpperCase() : DI[two];
      i++;
      continue;
    }
    const c = L2C[w[i].toLowerCase()];
    out += c ? (up ? c.toUpperCase() : c) : w[i];
  }
  return out;
}

// Latin → Bosnian Cyrillic. Text in parentheses (German terms), URLs and emails stay Latin.
export function toCyrillic(text) {
  if (/@|https?:|www\.|\.de\b/.test(text)) return text;
  return text
    .split(/(\([^)]*\))/)
    .map((seg) => (seg.startsWith("(") ? seg : seg.replace(/[A-Za-zČĆŽŠĐčćžšđäöüßÄÖÜ]+/g, word)))
    .join("");
}

const SKIP = "script,style,code,pre,[data-no-translit],[lang='de']";
const ATTRS = ["placeholder", "aria-label", "title"];

// Observes document.body while the public layout is mounted, so portaled drawers and toasts are covered too.
export function useTransliteration(root, active) {
  useEffect(() => {
    const el = root.current && document.body;
    if (!el || !active) return undefined;
    const orig = new Map();
    const attrOrig = new Map();
    const apply = (node) => {
      if (!node.nodeValue?.trim() || node.parentElement?.closest(SKIP)) return;
      const rec = orig.get(node);
      if (rec && node.nodeValue === rec.set) return;
      const source = node.nodeValue;
      const set = toCyrillic(source);
      orig.set(node, { source, set });
      if (set !== source) node.nodeValue = set;
    };
    const applyAttr = (e, at) => {
      const v = e.getAttribute(at);
      if (!v || e.closest(SKIP)) return;
      const recs = attrOrig.get(e) || {};
      if (recs[at] && v === recs[at].set) return;
      recs[at] = { source: v, set: toCyrillic(v) };
      attrOrig.set(e, recs);
      if (recs[at].set !== v) e.setAttribute(at, recs[at].set);
    };
    const applyAttrs = (n) => {
      if (n.nodeType !== 1) return;
      [n, ...n.querySelectorAll("[placeholder],[aria-label],[title]")].forEach((e) => ATTRS.forEach((at) => applyAttr(e, at)));
    };
    const walk = (n) => {
      if (n.nodeType === 3) return apply(n);
      applyAttrs(n);
      const w = document.createTreeWalker(n, NodeFilter.SHOW_TEXT);
      while (w.nextNode()) apply(w.currentNode);
    };
    walk(el);
    const obs = new MutationObserver((muts) => {
      muts.forEach((m) => {
        if (m.type === "characterData") apply(m.target);
        else if (m.type === "attributes") applyAttr(m.target, m.attributeName);
        else m.addedNodes.forEach(walk);
      });
    });
    obs.observe(el, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    return () => {
      obs.disconnect();
      orig.forEach((rec, node) => {
        if (node.nodeValue === rec.set) node.nodeValue = rec.source;
      });
      attrOrig.forEach((recs, e) => {
        Object.entries(recs).forEach(([at, r]) => {
          if (e.getAttribute(at) === r.set) e.setAttribute(at, r.source);
        });
      });
    };
  }, [root, active]);
}

const Ctx = createContext({ script: "latn", setScript: () => {} });

export function ScriptProvider({ children }) {
  const [script, setScript] = useState(() => localStorage.getItem("bp-script") || "latn");
  useEffect(() => {
    localStorage.setItem("bp-script", script);
    document.documentElement.lang = script === "cyrl" ? "bs-Cyrl" : "bs";
  }, [script]);
  return <Ctx.Provider value={{ script, setScript }}>{children}</Ctx.Provider>;
}

export const useScript = () => useContext(Ctx);

export const ScriptToggle = ({ className = "" }) => {
  const { script, setScript } = useScript();
  const btn = (v, label) => (
    <button type="button" onClick={() => setScript(v)} aria-pressed={script === v}
      className={`px-2 py-0.5 text-[11px] font-bold tracking-wider transition-colors ${script === v ? "bg-white text-navy" : "opacity-75 hover:opacity-100"}`}
      data-testid={`script-toggle-${v}`}>{label}</button>
  );
  return (
    <div role="group" aria-label="Pismo" className={`inline-flex overflow-hidden rounded-sm border border-current/40 ${className}`} data-no-translit>
      {btn("latn", "LAT")}{btn("cyrl", "ЋИР")}
    </div>
  );
};
